import type { ReactNode } from 'react';
import { TextField } from '@/components/form';
import type { CoefficientTarget } from '@/features/catalog/components/SaveCatalogCoefficientPrompt';

export interface CoefficientFieldProps {
  /** RHF field name. */
  name: string;
  label: ReactNode;
  /**
   * The catalog row this markup is being typed against, or null while nothing
   * is picked — a coefficient with no item behind it has no default to become.
   */
  target: CoefficientTarget | null;
  /** From `useCatalogCoefficientPrompt`. */
  ask: (target: CoefficientTarget, coefficient: string) => void;
  className?: string;
  containerClassName?: string;
  required?: boolean;
}

/**
 * A markup input that offers, on the way out, to make the number it holds the
 * catalog's default for the item it belongs to.
 *
 * The offer hangs off `onBlur` rather than `onChange`: mid-typing, "3" on its
 * way to "3.2" is a different number from the catalog's and would ask about a
 * value the user never meant. See `useCatalogCoefficientPrompt`.
 */
export function CoefficientField({
  name,
  label,
  target,
  ask,
  className,
  containerClassName,
  required,
}: CoefficientFieldProps) {
  return (
    <TextField
      name={name}
      label={label}
      type="number"
      inputMode="decimal"
      min={0.01}
      step={0.1}
      className={className}
      containerClassName={containerClassName}
      required={required}
      onBlur={(event) => {
        if (!target) return;
        ask(target, event.currentTarget.value);
      }}
    />
  );
}
